import { Dispatch, SetStateAction, useEffect, useRef } from 'react';

import { TMSCustomer } from 'types/Load';
import { Maybe } from 'types/UtilityTypes';
import { injectSelectedObject } from 'utils/loadInfoAndBuilding';

export type SearchCustomersNonDestructive = (
  field: 'name' | 'externalTMSID',
  value: string
) => Promise<TMSCustomer[]>;

type UseEnsureTmsCustomerRowForExternalIdArgs = {
  externalTmsId: string | undefined;
  customers: Maybe<TMSCustomer[]>;
  setCustomers: Dispatch<SetStateAction<Maybe<TMSCustomer[]>>>;
  tmsID: number | undefined;
  isLoadingCustomers: boolean;
  searchCustomersNonDestructive: SearchCustomersNonDestructive;
  /**
   * When this changes (e.g. new load-building suggestion), clear fetch de-dupe
   * so the same external id can be resolved again.
   */
  resetToken?: string | number;
};

/**
 * Ensures the TMS customer row for `externalTmsId` exists in `customers`.
 * Used for Customer and Bill To selects: the dropdown only stores externalTMSID;
 * address fields hydrate from the matching row in `customers`. After search +
 * reset, the row may be missing — this hook fetches by externalTMSID and injects.
 */
export function useEnsureTmsCustomerRowForExternalId({
  externalTmsId,
  customers,
  setCustomers,
  tmsID,
  isLoadingCustomers,
  searchCustomersNonDestructive,
  resetToken,
}: UseEnsureTmsCustomerRowForExternalIdArgs): void {
  const attemptedRef = useRef<string>('');

  useEffect(() => {
    attemptedRef.current = '';
  }, [resetToken]);

  useEffect(() => {
    if (isLoadingCustomers || !tmsID) {
      return;
    }

    if (!externalTmsId) {
      attemptedRef.current = '';
      return;
    }

    const alreadyPresent = (customers ?? []).some(
      (c) => c.externalTMSID === externalTmsId
    );
    if (alreadyPresent) {
      attemptedRef.current = '';
      return;
    }

    if (attemptedRef.current === externalTmsId) {
      return;
    }
    attemptedRef.current = externalTmsId;

    let cancelled = false;
    void (async () => {
      const searchedCustomers = await searchCustomersNonDestructive(
        'externalTMSID',
        externalTmsId
      );
      if (cancelled) return;
      const exactMatch = searchedCustomers.find(
        (c) => c.externalTMSID === externalTmsId
      );
      if (exactMatch) {
        setCustomers((prevCustomers) =>
          injectSelectedObject(exactMatch, prevCustomers ?? [])
        );
      }
    })();
    return () => {
      cancelled = true;
      if (attemptedRef.current === externalTmsId) {
        attemptedRef.current = '';
      }
    };
  }, [
    isLoadingCustomers,
    tmsID,
    externalTmsId,
    customers,
    searchCustomersNonDestructive,
    setCustomers,
  ]);
}
